import { useEffect, useState, useRef, useCallback } from 'react';
import { XMarkIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { createEvent, updateEvent } from '../../moduleServices/planner_events_api';
import SpinnerLoad from '../../moduleComponents/SpinnerLoad';
import ConfirmModal from '../../moduleComponents/ConfirmModal';
import DatePicker from 'shared_items/DatePicker';
import TimePicker from '../../moduleComponents/TimePicker';
import { useClickOutside } from 'services/useClickOutside';
import { toDatePart, toTimePart, toDateTime } from 'shared_items/formatConventions';
import {
  eventIsAllDay,
  eventEndInclusiveKey,
  eventStartKey,
  dateKeyPlusOneDay,
} from '../../tab_calendar/viewServices/calendar_grid_utils';
import { PLANNER_TIME_FORMAT_24 } from '../../moduleServices/planner_time_format';

/** Map API PlannerEvent → form state (includes Google all-day: exclusive last day → inclusive UI range). */
function mapEventToFormData(ev) {
  if (!ev) {
    return {
      title: '',
      description: '',
      is_all_day: false,
      date_range: { start: '', end: '' },
      time_range: { start: '12:00', end: '12:00' },
      calendar_id: null,
    };
  }

  if (eventIsAllDay(ev)) {
    const start = eventStartKey(ev);
    const endInc = eventEndInclusiveKey(ev) || start;
    return {
      title: ev.title || '',
      description: ev.description || '',
      is_all_day: true,
      date_range: { start, end: endInc || start },
      time_range: { start: '', end: '' },
      calendar_id: ev.calendar?.id ?? null,
    };
  }

  const startDate = toDatePart(ev.start_datetime);
  const startTime = toTimePart(ev.start_datetime);
  const endDate = toDatePart(ev.end_datetime || ev.start_datetime);
  const endTime = toTimePart(ev.end_datetime || ev.start_datetime);
  return {
    title: ev.title || '',
    description: ev.description || '',
    is_all_day: false,
    date_range: { start: startDate, end: endDate || startDate },
    time_range: { start: startTime || '09:00', end: endTime || startTime || '09:00' },
    calendar_id: ev.calendar?.id ?? null,
  };
}

function getCalendarTimezone(calendars, calendarId) {
  if (calendarId == null) return '';
  const c = calendars?.find((x) => x.id === calendarId);
  const tz = c?.iana_timezone;
  return typeof tz === 'string' ? tz.trim() : '';
}

/**
 * Timing fields for PlannerEvent API (matches PlannerEventSerializer.validate).
 * All-day: Google-style exclusive end_date (stored in DB).
 */
function buildTimingPayload(formData, calendars) {
  const tz = getCalendarTimezone(calendars, formData.calendar_id).slice(0, 64);
  if (formData.is_all_day) {
    const startInc = formData.date_range?.start?.trim?.() || '';
    const endInc = (formData.date_range?.end || startInc).trim?.() || startInc || '';
    if (!startInc) return { error: 'Start date is required' };
    if (endInc < startInc) return { error: 'End date must be on or after start date.' };
    const endExclusive = dateKeyPlusOneDay(endInc);
    if (!endExclusive) return { error: 'Invalid date range' };
    return {
      payload: {
        is_all_day: true,
        start_date: startInc,
        end_date: endExclusive,
        start_datetime: null,
        end_datetime: null,
        start_timezone: tz,
        end_timezone: tz,
      },
    };
  }

  const endDateEff = formData.date_range?.end || formData.date_range?.start;
  const endTimeEff = formData.time_range?.end || formData.time_range?.start;
  const startDateTime = toDateTime(formData.date_range?.start, formData.time_range?.start);
  if (!startDateTime) return { error: 'Start date and time are required' };
  const endDateTime = toDateTime(endDateEff, endTimeEff);
  if (!endDateTime) return { error: 'End date and time are required' };
  if (new Date(endDateTime) < new Date(startDateTime)) {
    return { error: 'End date/time must be after start date/time' };
  }

  return {
    payload: {
      is_all_day: false,
      start_datetime: startDateTime,
      end_datetime: endDateTime,
      start_date: null,
      end_date: null,
      start_timezone: tz,
      end_timezone: tz,
    },
  };
}

const emptyFormDefaults = () => ({
  title: '',
  description: '',
  is_all_day: false,
  date_range: { start: '', end: '' },
  time_range: { start: '12:00', end: '12:00' },
  calendar_id: null,
});

function ModalEventForm({
  isOpen,
  onClose,
  onOptimisticUpdate,
  editEvent,
  onDelete,
  calendars,
  defaultCalendarId = null,
  initialFormData = null,
  timeFormatPreference = PLANNER_TIME_FORMAT_24,
}) {
  const [formData, setFormData] = useState(emptyFormDefaults);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showCalendarDropdown, setShowCalendarDropdown] = useState(false);
  const calendarDropdownRef = useRef(null);
  const formDataRef = useRef(formData);

  useClickOutside([calendarDropdownRef], () => setShowCalendarDropdown(false), showCalendarDropdown);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  /** Fade in/out só depende de isOpen para não piscar quando initialFormData muda de referência. */
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (showDeleteModal) {
        setShowDeleteModal(false);
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, showDeleteModal, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setIsVisible(false);
      return;
    }
    setIsVisible(false);
    const rafId = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(rafId);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setIsSubmitting(false);
      setFormData(emptyFormDefaults());
      setShowCalendarDropdown(false);
      return;
    }

    setShowDeleteModal(false);
    setIsSubmitting(false);

    if (editEvent) {
      setFormData(mapEventToFormData(editEvent));
      setIsEditMode(false);
    } else {
      const base = emptyFormDefaults();
      if (initialFormData) {
        setFormData({
          ...base,
          is_all_day: initialFormData.is_all_day === true,
          date_range: initialFormData.date_range || { start: '', end: '' },
          time_range: initialFormData.time_range || { start: '12:00', end: '12:00' },
          calendar_id:
            initialFormData.calendar_id !== undefined
              ? initialFormData.calendar_id
              : defaultCalendarId,
        });
      } else {
        setFormData({
          ...base,
          calendar_id: defaultCalendarId,
        });
      }
      setIsEditMode(true);
    }
  }, [editEvent?.id, isOpen, defaultCalendarId, initialFormData]);

  const toggleAllDay = useCallback(() => {
    setFormData((prev) => ({ ...prev, is_all_day: !prev.is_all_day }));
  }, []);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (name === 'date_range') return { ...prev, date_range: value };
      if (name === 'time_range') return { ...prev, time_range: value };
      return { ...prev, [name]: value };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const currentFormData = formDataRef.current;

    if (!currentFormData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (!currentFormData.date_range.start?.trim?.()) {
      toast.error(currentFormData.is_all_day ? 'Start date is required' : 'Start date is required');
      return;
    }

    const timing = buildTimingPayload(currentFormData, calendars);
    if (timing.error) {
      toast.error(timing.error);
      return;
    }
    const { payload: timingPayload } = timing;

    const calendarIdPayload =
      currentFormData.calendar_id === null || currentFormData.calendar_id === undefined
        ? null
        : currentFormData.calendar_id;

    setIsSubmitting(true);
    try {
      const selectedCalendar = calendarIdPayload
        ? calendars.find((c) => c.id === calendarIdPayload)
        : null;

      if (editEvent) {
        const optimisticTiming = timingPayload.is_all_day
          ? {
              is_all_day: true,
              start_date: timingPayload.start_date,
              end_date: timingPayload.end_date,
              start_datetime: null,
              end_datetime: null,
              start_timezone: timingPayload.start_timezone,
              end_timezone: timingPayload.end_timezone,
            }
          : {
              is_all_day: false,
              start_datetime: timingPayload.start_datetime,
              end_datetime: timingPayload.end_datetime,
              start_date: null,
              end_date: null,
              start_timezone: timingPayload.start_timezone,
              end_timezone: timingPayload.end_timezone,
            };

        const optimisticData = {
          id: editEvent.id,
          title: currentFormData.title,
          description: currentFormData.description,
          ...optimisticTiming,
          calendar: selectedCalendar || null,
          calendar_id: calendarIdPayload,
        };
        if (onOptimisticUpdate) onOptimisticUpdate(optimisticData);
        setIsEditMode(false);

        const body = {
          title: currentFormData.title.trim(),
          description: currentFormData.description,
          calendar_id: calendarIdPayload,
          ...timingPayload,
        };

        const updatedEvent = await updateEvent(editEvent.id, body);
        toast.success('Event updated successfully!');
        if (onOptimisticUpdate) {
          onOptimisticUpdate({
            id: editEvent.id,
            ...updatedEvent,
            calendar: updatedEvent.calendar || selectedCalendar || null,
          });
        }
      } else {
        const body = {
          title: currentFormData.title.trim(),
          description: currentFormData.description,
          calendar_id: calendarIdPayload,
          ...timingPayload,
        };

        const newEvent = await createEvent(body);
        toast.success('Event created successfully!');
        const mergedCal =
          newEvent.calendar ||
          (calendarIdPayload ? calendars.find((c) => c.id === calendarIdPayload) : null);
        if (onOptimisticUpdate) {
          onOptimisticUpdate({
            ...newEvent,
            calendar: mergedCal || null,
            calendar_id: newEvent.calendar?.id ?? calendarIdPayload ?? null,
            created_at: newEvent.created_at || new Date().toISOString(),
            updated_at: newEvent.updated_at || new Date().toISOString(),
          });
        }
        onClose();
      }
    } catch (error) {
      const msg =
        error.response?.data?.detail ||
        error.response?.data?.non_field_errors?.[0] ||
        error.response?.data?.error ||
        error.message ||
        'Failed to save event';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save event');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditModeToggle = () => {
    if (editEvent) {
      setFormData(mapEventToFormData(editEvent));
    }
    setIsEditMode(true);
    setShowCalendarDropdown(false);
  };

  const handleCancelEdit = () => {
    if (editEvent) {
      setFormData(mapEventToFormData(editEvent));
      setIsEditMode(false);
    } else {
      onClose();
    }
    setShowCalendarDropdown(false);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteModal(false);
    setIsSubmitting(true);
    try {
      await onDelete(editEvent.id);
      toast.success('Event deleted successfully!');
      onClose();
    } catch (error) {
      toast.error('Failed to delete event');
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-200 ease-out ${
        isVisible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
    >
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

      <div
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col z-10"
      >
        <div className="flex items-center justify-between p-2 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            {editEvent ? (isEditMode ? 'Edit Event' : 'Event Details') : 'New Event'}
          </h2>
          <div className="flex items-center gap-2">
            {editEvent && !isEditMode && (
              <>
                <button
                  type="button"
                  onClick={handleEditModeToggle}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  title="Edit"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  title="Delete"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-2">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  name="title"
                  value={isEditMode ? formData.title : editEvent?.title || ''}
                  onChange={handleChange}
                  disabled={!isEditMode}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-700 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Event title"
                  required
                />
                <div className="relative flex-shrink-0" ref={calendarDropdownRef}>
                  {isEditMode ? (
                    <>
                      <button
                        type="button"
                        onClick={(evt) => {
                          evt.stopPropagation();
                          evt.preventDefault();
                          setShowCalendarDropdown(!showCalendarDropdown);
                        }}
                        className="w-8 h-8 rounded-full border-2 hover:opacity-80 transition-opacity flex items-center justify-center shadow-sm"
                        style={(() => {
                          const selectedCalendarId = formData.calendar_id;
                          const selectedCalendar = selectedCalendarId
                            ? calendars.find((c) => c.id === selectedCalendarId)
                            : null;
                          const calendarColor = selectedCalendar?.color || '#9CA3AF';
                          const borderColor = selectedCalendar ? calendarColor : '#D1D5DB';
                          return {
                            backgroundColor: calendarColor,
                            borderColor,
                          };
                        })()}
                        title={(() => {
                          const sid = formData.calendar_id;
                          const sel = sid ? calendars.find((c) => c.id === sid) : null;
                          return sel?.name || 'No calendar — click to assign';
                        })()}
                      />
                      {showCalendarDropdown && (
                        <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[200px] z-50 max-h-60 overflow-y-auto">
                          <button
                            type="button"
                            onClick={(evt) => {
                              evt.stopPropagation();
                              evt.preventDefault();
                              setShowCalendarDropdown(false);
                              setFormData((prev) => ({ ...prev, calendar_id: null }));
                            }}
                            className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors ${
                              !formData.calendar_id ? 'text-green-600 bg-green-50' : 'text-gray-700'
                            }`}
                          >
                            <div className="w-3 h-3 rounded-full border border-gray-300" />
                            <span>No calendar</span>
                          </button>
                          {calendars.map((calendar) => (
                            <button
                              key={calendar.id}
                              type="button"
                              onClick={(evt) => {
                                evt.stopPropagation();
                                evt.preventDefault();
                                setShowCalendarDropdown(false);
                                setFormData((prev) => ({ ...prev, calendar_id: calendar.id }));
                              }}
                              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors ${
                                formData.calendar_id === calendar.id
                                  ? 'text-green-600 bg-green-50'
                                  : 'text-gray-700'
                              }`}
                            >
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: calendar.color }}
                              />
                              <span>{calendar.name}</span>
                            </button>
                          ))}
                          {calendars.length === 0 && (
                            <div className="px-4 py-2 text-xs text-gray-500 italic">
                              No calendars available
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div
                      className="w-8 h-8 rounded-full border-2 flex-shrink-0"
                      style={(() => {
                        const calendar = editEvent?.calendar;
                        const calendarColor = calendar?.color || '#9CA3AF';
                        const borderColor = calendar ? calendarColor : '#D1D5DB';
                        return {
                          backgroundColor: calendarColor,
                          borderColor,
                        };
                      })()}
                      title={editEvent?.calendar?.name || 'No calendar'}
                    />
                  )}
                </div>
              </div>
            </div>

            {isEditMode && (
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.is_all_day}
                  aria-label="All day"
                  onClick={toggleAllDay}
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                    formData.is_all_day ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                      formData.is_all_day ? 'translate-x-4' : 'translate-x-0.5'
                    }`}
                  />
                </button>
                <span className="text-sm font-medium text-gray-800">All day</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <DatePicker
                name="date_range"
                value={formData.date_range}
                onChange={handleChange}
                mode="range"
                colorScheme="blue"
                disabled={!isEditMode}
                required={!!isEditMode}
                className="w-full"
              />
            </div>

            {!formData.is_all_day && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time *</label>
                <TimePicker
                  name="time_range"
                  value={formData.time_range}
                  onChange={handleChange}
                  disabled={!isEditMode}
                  required={isEditMode}
                  className="w-full"
                  timeFormatPreference={timeFormatPreference}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                name="description"
                value={isEditMode ? formData.description : editEvent?.description || ''}
                onChange={handleChange}
                disabled={!isEditMode}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-700 disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
                placeholder="Event description"
              />
            </div>
          </div>

          {isEditMode && (
            <div className="flex justify-end gap-3 mt-4 pt-2 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCancelEdit}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={isSubmitting}
              >
                {isSubmitting && <SpinnerLoad size="small" />}
                {editEvent ? 'Save Changes' : 'Create Event'}
              </button>
            </div>
          )}
        </form>

        {showDeleteModal && (
          <ConfirmModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={handleConfirmDelete}
            title="Delete Event"
            message="Are you sure you want to delete this event? This action cannot be undone."
            confirmText="Delete"
            cancelText="Cancel"
          />
        )}
      </div>
    </div>
  );
}

export default ModalEventForm;
